import { healthResponseSchema, type HealthResponse } from "@repo/shared"
import { useEffect, useState } from "react"

type HealthState = {
  data: HealthResponse | null
  error: Error | null
  isLoading: boolean
}

/**
 * Pings the backend's `/health` endpoint and validates the payload with the
 * schema both sides share, so a contract change fails loudly here.
 */
export function useHealth(): HealthState {
  const [state, setState] = useState<HealthState>({
    data: null,
    error: null,
    isLoading: true,
  })

  useEffect(() => {
    const controller = new AbortController()

    async function load() {
      try {
        const response = await fetch("/api/health", {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const data = healthResponseSchema.parse(await response.json())
        setState({ data, error: null, isLoading: false })
      } catch (error) {
        if (controller.signal.aborted) {
          return
        }

        setState({
          data: null,
          error: error instanceof Error ? error : new Error(String(error)),
          isLoading: false,
        })
      }
    }

    void load()

    return () => controller.abort()
  }, [])

  return state
}
